import { phoneNumberRules } from 'constants/form'
import {
  allSymbolsAndEnglishLettersRegex,
  allSymbolsAndEnglishLettersWithoutSemicolonRegex,
  cityRegex,
  latitudeRegex,
  locationRegex,
  longitudeRegex,
  mailRegex,
} from 'constants/regex'
import { UserRoles } from 'constants/roles'
import { LOCATION_ADDING_FORM_VALUES } from 'texts/locationDetails'
import { FormValuesSetting } from 'types/form'

export const formValuesSetting: FormValuesSetting = {
  state: {
    required: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    disabled: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    validationRules: [
      {
        type: 'pattern',
        value: allSymbolsAndEnglishLettersRegex,
        message: LOCATION_ADDING_FORM_VALUES.STATE.VALIDATION_MESSAGE,
      },
      {
        type: 'pattern',
        value: allSymbolsAndEnglishLettersWithoutSemicolonRegex,
        message:
          LOCATION_ADDING_FORM_VALUES.STATE.ADDITIONAL_VALIDATION_MESSAGE,
      },
    ],
  },
  street: {
    required: {
      [UserRoles.Supplier]: true,
      [UserRoles.Operator]: true,
    },
    disabled: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    validationRules: [
      {
        type: 'required',
        message: LOCATION_ADDING_FORM_VALUES.STREET.REQUIRED_MESSAGE,
      },
      {
        type: 'pattern',
        value: allSymbolsAndEnglishLettersRegex,
        message: LOCATION_ADDING_FORM_VALUES.STREET.VALIDATION_MESSAGE,
      },
      {
        type: 'max',
        value: 100,
        message: LOCATION_ADDING_FORM_VALUES.STREET.MAX_VALIDATION_MESSAGE,
      },
      {
        type: 'pattern',
        value: allSymbolsAndEnglishLettersWithoutSemicolonRegex,
        message:
          LOCATION_ADDING_FORM_VALUES.STREET.ADDITIONAL_VALIDATION_MESSAGE,
      },
    ],
  },
  building: {
    required: {
      [UserRoles.Supplier]: true,
      [UserRoles.Operator]: true,
    },
    disabled: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    validationRules: [
      {
        type: 'required',
        message: LOCATION_ADDING_FORM_VALUES.BUILDING.REQUIRED_MESSAGE,
      },
      {
        type: 'pattern',
        value: allSymbolsAndEnglishLettersRegex,
        message: LOCATION_ADDING_FORM_VALUES.BUILDING.VALIDATION_MESSAGE,
      },
      {
        type: 'max',
        value: 30,
        message: LOCATION_ADDING_FORM_VALUES.BUILDING.MAX_VALIDATION_MESSAGE,
      },
      {
        type: 'pattern',
        value: allSymbolsAndEnglishLettersWithoutSemicolonRegex,
        message:
          LOCATION_ADDING_FORM_VALUES.BUILDING.ADDITIONAL_VALIDATION_MESSAGE,
      },
    ],
  },
  latitude: {
    required: {
      [UserRoles.Supplier]: true,
      [UserRoles.Operator]: true,
    },
    disabled: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    validationRules: [
      {
        type: 'required',
        message: LOCATION_ADDING_FORM_VALUES.LATITUDE.REQUIRED_MESSAGE,
      },
      {
        type: 'pattern',
        value: latitudeRegex,
        message: LOCATION_ADDING_FORM_VALUES.LATITUDE.VALIDATION_MESSAGE,
      },
      {
        type: 'custom',
        message: LOCATION_ADDING_FORM_VALUES.LATITUDE.MIN_VALUE_MESSAGE,
        value: (value: string) => !Number(value),
      },
    ],
  },
  countryId: {
    required: {
      [UserRoles.Supplier]: true,
      [UserRoles.Operator]: true,
    },
    disabled: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    validationRules: [
      {
        type: 'required',
        message: LOCATION_ADDING_FORM_VALUES.COUNTRY.REQUIRED_MESSAGE,
      },
    ],
  },
  longitude: {
    required: {
      [UserRoles.Supplier]: true,
      [UserRoles.Operator]: true,
    },
    disabled: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    validationRules: [
      {
        type: 'required',
        message: LOCATION_ADDING_FORM_VALUES.LONGITUDE.REQUIRED_MESSAGE,
      },
      {
        type: 'pattern',
        value: longitudeRegex,
        message: LOCATION_ADDING_FORM_VALUES.LONGITUDE.VALIDATION_MESSAGE,
      },
      {
        type: 'custom',
        message: LOCATION_ADDING_FORM_VALUES.LONGITUDE.MIN_VALUE_MESSAGE,
        value: (value: string) => !Number(value),
      },
    ],
  },
  postalCode: {
    required: {
      [UserRoles.Supplier]: true,
      [UserRoles.Operator]: true,
    },
    disabled: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    validationRules: [
      {
        type: 'required',
        message: LOCATION_ADDING_FORM_VALUES.POSTAL_CODE.REQUIRED_MESSAGE,
      },
      {
        type: 'pattern',
        value: allSymbolsAndEnglishLettersRegex,
        message: LOCATION_ADDING_FORM_VALUES.POSTAL_CODE.VALIDATION_MESSAGE,
      },
      {
        type: 'max',
        value: 16,
        message: LOCATION_ADDING_FORM_VALUES.POSTAL_CODE.MAX_VALIDATION_MESSAGE,
      },
      {
        type: 'pattern',
        value: allSymbolsAndEnglishLettersWithoutSemicolonRegex,
        message:
          LOCATION_ADDING_FORM_VALUES.POSTAL_CODE.ADDITIONAL_VALIDATION_MESSAGE,
      },
    ],
  },
  cityId: {
    required: {
      [UserRoles.Supplier]: true,
      [UserRoles.Operator]: true,
    },
    disabled: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    validationRules: [
      {
        type: 'required',
        message: LOCATION_ADDING_FORM_VALUES.CITY.REQUIRED_MESSAGE,
      },
      {
        type: 'pattern',
        value: cityRegex,
        message: LOCATION_ADDING_FORM_VALUES.CITY.VALIDATION_MESSAGE,
      },
      {
        type: 'max',
        value: 100,
        message: LOCATION_ADDING_FORM_VALUES.CITY_NAME.MAX_VALIDATION_MESSAGE,
      },
      {
        type: 'pattern',
        value: allSymbolsAndEnglishLettersWithoutSemicolonRegex,
        message: LOCATION_ADDING_FORM_VALUES.CITY.ADDITIONAL_VALIDATION_MESSAGE,
      },
    ],
  },
  locationId: {
    required: {
      [UserRoles.Supplier]: true,
      [UserRoles.Operator]: true,
    },
    disabled: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    validationRules: [
      {
        type: 'required',
        message: LOCATION_ADDING_FORM_VALUES.LOCATION.REQUIRED_MESSAGE,
      },
      {
        type: 'pattern',
        value: locationRegex,
        message: LOCATION_ADDING_FORM_VALUES.LOCATION.VALIDATION_MESSAGE,
      },
    ],
  },
  currencyID: {
    required: {
      [UserRoles.Supplier]: true,
      [UserRoles.Operator]: true,
    },
    disabled: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    validationRules: [
      {
        type: 'required',
        message: LOCATION_ADDING_FORM_VALUES.CURRENCY.REQUIRED_MESSAGE,
      },
    ],
  },
  phone: {
    required: {
      [UserRoles.Supplier]: true,
      [UserRoles.Operator]: true,
    },
    disabled: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    validationRules: phoneNumberRules,
  },
  email: {
    required: {
      [UserRoles.Supplier]: true,
      [UserRoles.Operator]: true,
    },
    disabled: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    validationRules: [
      {
        type: 'required',
        message: LOCATION_ADDING_FORM_VALUES.EMAIL.REQUIRED_MESSAGE,
      },
      {
        type: 'pattern',
        value: mailRegex,
        message: LOCATION_ADDING_FORM_VALUES.EMAIL.VALIDATION_MESSAGE,
      },
      {
        type: 'max',
        value: 254,
        message: LOCATION_ADDING_FORM_VALUES.EMAIL.MAX_VALIDATION_MESSAGE,
      },
    ],
  },
  pickUpInstruction: {
    required: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    disabled: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    validationRules: [
      {
        type: 'pattern',
        value: allSymbolsAndEnglishLettersRegex,
        message: LOCATION_ADDING_FORM_VALUES.POSTAL_CODE.VALIDATION_MESSAGE,
      },
      {
        type: 'max',
        value: 700,
        message:
          LOCATION_ADDING_FORM_VALUES.PICKUP_INSTRUCTIONS
            .MAX_VALIDATION_MESSAGE,
      },
    ],
  },
  dropOffInstruction: {
    required: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    disabled: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    validationRules: [
      {
        type: 'pattern',
        value: allSymbolsAndEnglishLettersRegex,
        message: LOCATION_ADDING_FORM_VALUES.POSTAL_CODE.VALIDATION_MESSAGE,
      },
      {
        type: 'max',
        value: 700,
        message:
          LOCATION_ADDING_FORM_VALUES.DROPOFF_INSTRUCTIONS
            .MAX_VALIDATION_MESSAGE,
      },
    ],
  },
  locationType: {
    required: {
      [UserRoles.Supplier]: true,
      [UserRoles.Operator]: true,
    },
    disabled: {
      [UserRoles.Supplier]: false,
      [UserRoles.Operator]: false,
    },
    validationRules: [
      {
        type: 'required',
        message: LOCATION_ADDING_FORM_VALUES.NEW_LOCATION_TYPE.REQUIRED_MESSAGE,
      },
    ],
  },
}

export const LOCATION_TYPE_AIRPORT = 'Airport'
export const LOCATION_TYPE_RAILWAY_STATION = 'Railway Station'
